import axios from "axios";
import { CompanyExportParams } from "../../pages/Company/components/CompanyExport/types";
import { ExportData } from "../../pages/Workspace/ExportModal/types";
import { axiosInstance } from "../../utils/axios";
import {
  ExportDetailsResponse,
  ExportDetailsFilters,
  ExportMetrics,
} from "../../types/niffler";

const API_URL = "/niffler-api/exports";

// Error messages mapping
export const NIFFLER_ERROR_MESSAGES = {
  INSUFFICIENT_CREDITS: {
    title: "Insufficient Credits",
    message:
      "You have insufficient credits available. Please contact your account manager to purchase additional credits.",
  },
  FILE_TOO_LARGE: {
    title: "Export Too Large",
    message:
      "The volume of data in this export exceeds the maximum size. We recommend adding additional filters in order to narrow your search results.",
  },
} as const;

export type NifflerErrorCode = keyof typeof NIFFLER_ERROR_MESSAGES;

// Custom error class
export class NifflerExportError extends Error {
  constructor(
    public code: NifflerErrorCode,
    message: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = "NifflerExportError";
  }
}

// Wrapper for Niffler API requests with error handling
async function nifflerRequest<T>(request: Promise<{ data: T }>): Promise<T> {
  try {
    const response = await request;
    return response.data;
  } catch (error) {
    // Throw directly instead of calling a function that throws
    if (axios.isAxiosError(error) && error.response?.data) {
      const { code, message } = error.response.data;
      const statusCode = error.response.status;

      let errorCode = code;
      if (statusCode === 422 && message) {
        const match = message.match(/^([A-Z_]+):/);

        errorCode = match ? match[1] : "UNKNOWN_ERROR";
      }

      throw new NifflerExportError(errorCode, message, statusCode);
    }
    throw error;
  }
}

export function getExportResults(
  data: ExportData | CompanyExportParams
): Promise<ExportData> {
  return nifflerRequest(axiosInstance.post(API_URL, data));
}

// Fetch paginated export history
export function fetchExportsHistory(
  filters: ExportDetailsFilters
): Promise<ExportDetailsResponse> {
  return nifflerRequest(axiosInstance.post(`${API_URL}/readByUser`, filters));
}

// Resubmit a failed export
export function resubmitExport(exportId: number): Promise<ExportData> {
  return nifflerRequest(
    axiosInstance.put(`${API_URL}/${exportId}/resubmitByUser`)
  );
}

// Get export metrics for the current user
export function fetchExportsMetrics(): Promise<ExportMetrics> {
  return nifflerRequest(axiosInstance.get(`${API_URL}/metrics`));
}
